import { ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { selectIsSupportChatTestFeatureFlagOn } from 'core/userContext/selectors';
import { useCampaign } from 'modules/workflowNodeEditors/components/sendNodeEditors/BaseSendEditor/BaseSendEditor.hooks';
import { NodeData } from 'modules/workflows/types';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button, SecondaryButton } from 'ui/button';
import { LongArrowLeft, LongArrowRight } from 'ui/icons';
import noop from 'utils/noop';
import CampaignButton from './CampaignButton/CampaignButton';
import {
  StyledButtons,
  StyledDrawerFooter,
  StyledDrawerFooterNoChat,
  StyledOpenNewWindowIcon,
} from './NodeEditor.styled';

type NodeEditorFooterProps = {
  areActionsDisabled?: boolean;
  /** Additional buttons for the footer */
  footerButtons?: ReactNode;
  isSendNodeEditor: boolean;
  isWorkflowPublished?: boolean;
  nodeData: Partial<NodeData>;
  onBack?: () => void;
  onCloseEditor?: () => void;
  onContinue?: () => void;
  onNodeUpdate?: () => void;
};

export const CONTINUE_BTN_TEST_ID = 'node-editor-continue-btn';
export const BACK_BTN_TEST_ID = 'node-editor-back-btn';
export const UPDATE_BTN_TEST_ID = 'node-editor-update-btn';
export const CAMPAIGN_ANALYTICS_BUTTON_TEST_ID = 'campaign-analytics-button';
export const NODE_EDITOR_FOOTER_TEST_ID = 'node-editor-footer';

export const getFooterButtonsConfig = ({
  onBack,
  onContinue,
  isSendNodeEditor,
  campaignId,
}: Omit<NodeEditorFooterProps, 'nodeData'> & { campaignId?: number }) => {
  const showContinueButton = typeof onContinue === 'function';
  const showBackButton = typeof onBack === 'function';

  return {
    showBackButton,
    showContinueButton,
    showCampaignButton: !!campaignId,
    showCampaignUpdateButton: !showContinueButton && isSendNodeEditor,
    showUpdateButton: !showContinueButton && !isSendNodeEditor,
  };
};

export const NodeEditorFooter = ({
  areActionsDisabled = false,
  footerButtons,
  isSendNodeEditor = false,
  isWorkflowPublished = false,
  nodeData,
  onBack,
  onCloseEditor = noop,
  onContinue,
  onNodeUpdate = noop,
}: NodeEditorFooterProps) => {
  const isSupportChatEnabled = useSelector(selectIsSupportChatTestFeatureFlagOn);
  const FooterCmp = isSupportChatEnabled ? StyledDrawerFooter : StyledDrawerFooterNoChat;
  const campaignId = nodeData?.campaignId;

  const { setIsReadOnly: setIsCampaignReadOnly, isReadOnly: isCampaignReadOnly } = useCampaign();

  const config = getFooterButtonsConfig({ onBack, onContinue, isSendNodeEditor, campaignId });

  return (
    <FooterCmp
      data-test={`${NODE_EDITOR_FOOTER_TEST_ID}${isSupportChatEnabled ? '-with-chat' : ''}`}
    >
      {config.showBackButton && (
        <SecondaryButton
          isDisabled={areActionsDisabled || isWorkflowPublished}
          onClick={onBack}
          size={BUTTON_SIZES.LARGE}
          data-test={BACK_BTN_TEST_ID}
        >
          <LongArrowLeft height={24} width={24} /> Back
        </SecondaryButton>
      )}
      <StyledButtons $alignLeft={!isSupportChatEnabled}>
        {footerButtons}
        {config.showContinueButton && (
          <Button
            isDisabled={areActionsDisabled || isWorkflowPublished}
            onClick={onContinue}
            variant={BUTTON_VARIANTS.PRIMARY}
            size={BUTTON_SIZES.LARGE}
            data-test={CONTINUE_BTN_TEST_ID}
          >
            Continue <LongArrowRight height={24} width={24} />
          </Button>
        )}
        {/* Display campaign analytics button if there is a campaign */}
        {config.showCampaignButton && (
          <SecondaryButton
            data-test={CAMPAIGN_ANALYTICS_BUTTON_TEST_ID}
            size={BUTTON_SIZES.LARGE}
            // @ts-ignore
            as="a"
            href={`/analytics/campaignPerformance?campaignId=${campaignId}`}
            rel="noreferrer noopener"
            target="_blank"
          >
            View campaign analytics <StyledOpenNewWindowIcon />
          </SecondaryButton>
        )}
        {config.showCampaignUpdateButton && (
          <CampaignButton
            {...{
              onClick: onNodeUpdate,
              campaignId,
              isReadOnly: isCampaignReadOnly,
              isDisabled: areActionsDisabled,
              setIsReadOnly: setIsCampaignReadOnly,
              onCancel: onCloseEditor,
            }}
          />
        )}
        {config.showUpdateButton && (
          <Button
            isDisabled={areActionsDisabled || isWorkflowPublished}
            onClick={onNodeUpdate}
            variant={BUTTON_VARIANTS.PRIMARY}
            size={BUTTON_SIZES.LARGE}
            data-test={UPDATE_BTN_TEST_ID}
          >
            Update
          </Button>
        )}
      </StyledButtons>
    </FooterCmp>
  );
};
